<template>
    <!--
        CustomActionTable är i allt väsentligt en vanlig ActionTable, men renderar alla celler som CustomTableCell. Information om hur
        template ska se ut finns i den filen. CustomTable kan behöva anpassas för valbara rader o.d. Förhoppningsvis kan den ersätta både
        ActionTable och SelectActionTable i ett senare skede.
    -->
    <div class="action-table">
        <button @click="collapse = !collapse" v-if="collapseLabel" class="btn btn-default btn-block" type="button">
            <span :class="toggleCaret"></span>
            <span role="presentation">{{ collapseLabel }}</span>
            <span class="label" :key="label" v-for="label in labels">{{ label }}</span>
        </button>
        <collapse-transition>
            <div v-show="!collapse || !collapseLabel">
                <div class="container" v-if="items.length > 0 && showScrollIcon">
                    <div class="scroll-icon">&#x2194;</div>
                </div>
                <table class="table">
                    <thead>
                        <tr>
                            <!--
        Sortable should be reworked.
    -->
                            <th
                                v-for="col in columns.filter((col) => col.lock !== 'right')"
                                :key="col.name"
                                class="table-heading"
                                :class="{ 'lock-left': col.lock === 'left' }"
                            >
                                <template v-if="!col.hasSorting">
                                    <span class="col-title">
                                        {{ col.title }}
                                    </span>
                                </template>
                                <template v-else>
                                    <button
                                        type="button"
                                        class="style-sorting-button"
                                        @click="sort(col.name)"
                                        @mouseover="hoverColumn = col.name"
                                        @mouseleave="hoverColumn = ''"
                                    >
                                        <span class="col-title col-title-button">
                                            {{ col.title }}
                                        </span>
                                        <img :src="addIcon(col.name)" class="dropdown-icon" />
                                    </button>
                                </template>
                            </th>
                            <th v-if="actions?.length > 0" class="table-heading lock-right">
                                <div>
                                    <template v-for="col in columns.filter((col) => col.lock === 'right')">
                                        <span v-if="!col.hasSorting" class="col-title" :key="col.name">
                                            {{ col.title }}
                                        </span>
                                        <button
                                            v-else
                                            :key="col.name"
                                            type="button"
                                            class="style-sorting-button"
                                            @click="sort(col.name)"
                                            @mouseover="hoverColumn = col.name"
                                            @mouseleave="hoverColumn = ''"
                                        >
                                            <span class="col-title col-title-button">
                                                {{ col.title }}
                                            </span>
                                            <img :src="addIcon(col.name)" class="dropdown-icon" />
                                        </button>
                                    </template>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in items"
                            :actions="actions"
                            :columns="columns"
                            :key="item.id"
                            :class="{
                                'table-row': true,
                                'disabled-row': getDisabledRow(item),
                                'selectable-row': selectableRows,
                                'selected-row': selectedItem === item,
                            }"
                            @click.prevent="emitSelect($event, item)"
                        >
                            <template
                                v-for="(col, index) in columns.filter((col) => !lockColumns || col.lock !== 'right')"
                            >
                                <custom-table-cell
                                    v-if="typeof col.template === 'function'"
                                    :template="col.template(item)"
                                    :key="col.name + '-' + index"
                                    class="custom-table-body-td"
                                    :class="{
                                        'lock-left': col.lock === 'left',
                                        cell: col.lock === 'left',
                                        selected: selectedItem === item,
                                    }"
                                />
                                <!-- Also the default template in custom-table-cell, but reducing the number of child components for eaiser debugging, performance impact is negligible -->
                                <td
                                    v-else
                                    :key="`${col.name} + '-' + ${index}`"
                                    class="custom-table-body-td"
                                    :class="{
                                        'lock-left': col.lock === 'left',
                                        cell: col.lock === 'left',
                                        selected: selectedItem === item,
                                    }"
                                >
                                    <span>{{ item[col.name] }}</span>
                                </td>
                            </template>
                            <td
                                v-if="actions?.length > 0"
                                class="lock-right cell"
                                :class="{ selected: selectedItem === item }"
                            >
                                <div>
                                    <template v-for="(col, index) in columns.filter((col) => col.lock === 'right')">
                                        <custom-table-cell
                                            v-if="typeof col.template === 'function'"
                                            :template="col.template(item)"
                                            :key="col.name + '-' + index"
                                            class="custom-table-body-td"
                                        />
                                        <!-- Also the default template in custom-table-cell, but reducing the number of child components for eaiser debugging, performance impact is negligible -->
                                        <td v-else :key="`${col.name} + '-' + ${index}`" class="custom-table-body-td">
                                            <span>{{ item[col.name] }}</span>
                                        </td>
                                    </template>
                                    <actions-dropdown
                                        v-if="getActions(item).length"
                                        :id="item.id"
                                        :actions="getActions(item)"
                                        :item="item"
                                        @dropdownAction="dropdownAction"
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </collapse-transition>
    </div>
</template>

<script>
    import ActionTable from "@/components/generic/ActionTable.vue";
    import ActionsDropdown from "@/components/generic/ActionsDropdown.vue";
    import CustomTableCell from "@/components/generic/CustomTableCell.vue";
    import { cloneDeep } from "lodash";
    import multisort from "multisort";
    import SortTableAscending from "@/assets/sort_folder/sort_table_ascending.svg";
    import SortTableDescending from "@/assets/sort_folder/sort_table_descending.svg";
    import SortTableHover from "@/assets/sort_folder/sort_table_hover.svg";
    import SortTableEmpty from "@/assets/sort_folder/sort_table_empty.svg";

    export default {
        extends: ActionTable,
        components: {
            ActionsDropdown,
            CustomTableCell,
        },
        props: {
            lockColumns: Boolean,
        },
        data() {
            return {
                numberClicks: 0,
                sortColumn: "",
                sortOrder: "",
                hoverColumn: "",
                selectedRow: NaN,
                showScrollIcon: true,
                timeOutID: null,
            };
        },
        watch: {
            items(newVal, _oldVal) {
                if (newVal.length > 0 && this.showScrollIcon && !this.timeOutID) {
                    this.timeOutID = setTimeout(() => {
                        this.showScrollIcon = false;
                    }, 2500);
                }
            },
        },
        computed: {
            sortColumns() {
                return this.columns
                    .filter((el) => {
                        return typeof el.sortOrder !== "undefined" && el.sortOrder !== null;
                    })
                    .sort((a, b) => {
                        return a.sortOrder - b.sortOrder;
                    });
            },
            sortedItems() {
                let sortedItems = cloneDeep(this.items);
                let criteria = [];
                this.sortColumns.forEach((col) => {
                    if (col.sortMethod) criteria.push(col.sortMethod);
                    else criteria.push(col.name);
                });

                multisort(sortedItems, criteria);

                return sortedItems;
            },
        },
        methods: {
            hideMenu(item) {
                return item.status === "Genomförd" ? false : true;
            },
            getActions(item) {
                return this.actions.filter((el) => {
                    if (el.condition) {
                        return el.condition(item);
                    } else return true;
                });
            },
            sort(name) {
                if (this.sortColumn === name) {
                    this.sortOrder = this.sortOrder === "-" ? "" : "-";
                } else {
                    this.sortColumn = name;
                    this.sortOrder = "-";
                }

                this.$emit("sortingButton", { column: this.sortColumn, order: this.sortOrder });
            },
            addIcon(colName) {
                if (this.sortColumn === colName) {
                    if (this.sortOrder === "") {
                        return SortTableAscending;
                    } else {
                        return SortTableDescending;
                    }
                } else {
                    if (this.hoverColumn === colName) {
                        return SortTableHover;
                    } else {
                        return SortTableEmpty;
                    }
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/vendors/bootstrap";
    @import "@/style/deprecated_main";

    @keyframes fadeOut {
        from {
            opacity: 1;
        }
        to {
            opacity: 0;
        }
    }

    .action-table {
        overflow-x: auto;
        overflow-y: visible;
        margin-right: -$outer-form-spacing;
        position: relative;

        .table-heading {
            background-color: $color-light;
            vertical-align: middle;
        }

        .container {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;

            .scroll-icon {
                display: inline-block;
                background-color: #354052;
                padding: 5px;
                border-radius: 20px;
                font-size: 30px;
                color: white;
                box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
                width: 60px;
                text-align: center;
                position: absolute;
                top: 60px;
                animation: fadeOut 1.5s forwards;
                animation-delay: 1s;
            }
        }

        table {
            border-bottom: 1px solid #dee2e6;

            thead span {
                font-family: Roboto Medium;
            }

            thead th {
                border-top: none;
            }

            .custom-table-body-td {
                font-size: 14px;
                line-height: 16px;
                font-family: Roboto;
                padding: 8px 10px;
                vertical-align: middle;
            }
        }

        .lock-left,
        .lock-right {
            position: sticky;
            background-color: $color-light;
        }

        .lock-left {
            left: 0;
        }

        .lock-right {
            right: 0;
        }

        .lock-right:not(.table-heading) {
            > div:first-child {
                display: grid;
                grid-auto-flow: column;
                align-items: center;

                > div:last-child {
                    padding-left: 0.5rem;
                    padding-right: 0.5rem;
                    margin-left: auto;
                }

                > div.dropdown {
                    padding-right: 1rem;
                }

                td {
                    padding: 0;
                    border: none;
                }
            }
        }

        .lock-right.actions {
            padding-right: 1rem;
        }

        // Måste vara ::before för att rendera box-shadow på sticky i Chrome
        .lock-right.cell::before,
        .lock-left.cell::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
        }

        .lock-right.cell::before {
            box-shadow: $drop-shadow-left;
        }

        .lock-left.cell::before {
            box-shadow: $drop-shadow-right;
        }

        .lock-right.selected,
        .lock-left.selected {
            background-color: #ebf6f8;
        }
    }

    .style-sorting-button,
    .table-heading > span {
        display: grid;
        grid-auto-flow: column;
        justify-items: start;
        align-items: center;
        padding: 0 10px;
        gap: 10px;
    }

    .style-sorting-button {
        background: transparent;
        border: none;
    }

    .style-sorting-button > img {
        max-height: 18px;
        max-width: 18px;
    }

    .style-sorting-button:hover {
        border-radius: 5px;
        background-color: #cee9f3;
    }

    .col-title {
        color: #277692;
        font-family: Roboto;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 17px;
        white-space: nowrap;
    }

    .selected-row {
        background-color: #ebf6f8;

        td:first-child::before {
            border-left: 4px solid #277692;
        }
    }

    .table-row {
        cursor: pointer;
    }
</style>
